import React, { useState, useEffect, useContext, useRef } from 'react';
import ModalEliminacion from '../modal/ModalEliminacion';
import { Get_type_tag_options } from '../Comun';
import { AuthContext } from '../contexts/AuthContext';

interface TagProps 
{
  tag?: 
  {
    id: number; 
    tag: string; 
    valid_time: string; 
    type_tag: number;
  };

  id_event: number;

  onSave: (tagData: 
  {
    id?: number; 
    tag: string; 
    valid_time: string; 
    type_tag: number;
  }) => void;

  onDelete?: (tagId: number) => void;
  
  onCancel: () => void;
}

const Etiqueta: React.FC<TagProps> = ({ tag, id_event, onSave, onDelete, onCancel }) => 
{
  const { token } = useContext(AuthContext);
  const [tagName, setTag] = useState('');
  const [validTime, setValidTime] = useState('');
  const [typeTag, setTypeTag] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null); // Ref para el contenedor del modal

   // Opciones para el selector
   const typeTagOptions = Get_type_tag_options();

  // Determinar si estamos en modo de edición o creación
  const isEditing = tag && tag.id !== -1;
  
  // Determinar si el input de tipo time debe estar deshabilitado
  const isTimeDisabled = typeTag === 2;
  
  //-----------------------------------------------------------

  useEffect(() => 
  {
    if (tag) 
    {
      setTag(tag.tag);
      setValidTime(tag.valid_time);
      setTypeTag(tag.type_tag);
    } 
    else 
    {
      // Inicializa el estado para un nuevo tag
      setTag('');
      setValidTime('');
      setTypeTag(0);
    }
  }, [tag]);

  //-----------------------------------------------------------

  useEffect(() => 
  {
    // Función para manejar clics fuera del componente
    const handleClickOutside = (event: MouseEvent) => 
    {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onCancel();
      }
    };

    // Función para manejar presiones de teclas
    const handleEscPress = (event: KeyboardEvent) => 
    {
      if (event.key === 'Escape') 
      {
        onCancel();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscPress);

    return () => 
    {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscPress);
    };
  }, [onCancel]);  

  //-----------------------------------------------------------

  const handleSubmit = async (e: React.FormEvent) => 
  {
    e.preventDefault();
    try 
    {
      const formData = new URLSearchParams();
      formData.append('tag', tagName);

      if (!isTimeDisabled)
        formData.append('valid_time', validTime);
      
      formData.append('type_tag', typeTag.toString());
      formData.append('id_event', id_event.toString());

      const url = isEditing ? 'web/update_event_tag' : 'web/create_event_tag';

      if (tag && tag.id !== null)
        formData.append('id', tag.id.toString());

      const api_url = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(api_url + url, {
        method: 'POST',
        headers: {
          'X-AUTH-TOKEN': token || '',
          'Content-Type': 'application/x-www-form-urlencoded'},
        body: formData.toString()});

      if (response.ok) 
      {
        onSave({
          id: tag?.id ?? undefined, 
          tag: tagName, 
          valid_time: validTime, 
          type_tag: typeTag});
      } 
      else 
      {
        const errorData = await response.json();
        setError(errorData.error || 'Error al guardar el tag.');
      }
    } 
    catch (error) 
    {
      setError('Error de red. Por favor, intente nuevamente.');
    }
  };

  //-----------------------------------------------------------

  const handleDelete = () => 
  {
    if (tag && tag.id !== null && tag.id !== -1) 
    {
      setIsModalOpen(true);
    }
  };

  //-----------------------------------------------------------

  const handleConfirmDelete = async () => 
  {
    if (tag && tag.id !== null && tag.id !== -1 && onDelete) 
    {
      try 
      {
        const formData = new URLSearchParams();
        formData.append('id', tag.id.toString());

        const api_url = process.env.REACT_APP_API_BASE_URL || '';
        const response = await fetch(`${api_url}web/delete_event_tag`, {
          method: 'POST',
          headers: {
            'X-AUTH-TOKEN': token || '',
            'Content-Type': 'application/x-www-form-urlencoded'},
          body: formData.toString()});

        if (response.ok) 
        {
          onDelete(tag.id);
          setIsModalOpen(false);
        } 
        else 
        {
          const errorData = await response.json();
          setError(errorData.error || 'Error al eliminar el tag.');
        }
      } 
      catch (error) 
      {
        setError('Error de red. Por favor, intente nuevamente.');
      }
    }
  };

  //-----------------------------------------------------------

  // Manejar el cambio en el selector
  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => 
  {
    setTypeTag(Number(event.target.value));
  };

  //-----------------------------------------------------------

  return (
    <div className="evento-container" ref={modalRef}>
      <h2>{isEditing ? 'Editar Tag' : 'Crear Nuevo Tag'}</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <input
            id="name"
            type="text"
            value={tagName}
            onChange={(e) => setTag(e.target.value)}
            placeholder="Nombre de la etiqueta"
            required
          />
        </div>
        <div className="form-row">
          <div className="form-group select-group">
            <select
              value={typeTag}
              onChange={handleSelectChange}
            >
              {typeTagOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <input
              id="valid_time"
              type="time"
              value={validTime}
              onChange={(e) => setValidTime(e.target.value)}
              title="Tiempo"
              disabled={isTimeDisabled} // Deshabilitar si typeTag es 2
              required={!isTimeDisabled} // Requerido solo si no está deshabilitado
            />
          </div>
        </div>
        {error && <p className="error-message">{error}</p>}
        <div className="form-actions">
          <button type="submit">{isEditing ? 'Guardar' : 'Crear'}</button>
          <button type="button" onClick={onCancel}>Cancelar</button>
          {isEditing && tag && tag.id !== null && tag.id !== -1 && (
            <div className="delete-button-container">
            <button type="button" className="delete-event-button" onClick={handleDelete}>
            <i className="fas fa-trash"></i>Borrar
            </button>
            </div>
          )}
        </div>
      </form>
      {isModalOpen && (
        <ModalEliminacion
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onConfirm={handleConfirmDelete}
        >
          <p>¿Estás seguro de que quieres eliminar este tag?</p>
        </ModalEliminacion>
      )}
    </div>
  );
};

export default Etiqueta;
